import EventEmitter from 'events';
import { RTCSession } from 'jssip/lib/RTCSession';
import { UserAgent } from './interfaces';

class Call {
  private userAgent: UserAgent;
  private streams: any;
  public tags: any;
  private options: any;
  private target: any;
  // @ts-expect-error
  public session: RTCSession;
  private emitter: EventEmitter;
  public on: (
    eventName: string | symbol,
    listener: (...args: any[]) => void,
  ) => EventEmitter;
  public once: (
    eventName: string | symbol,
    listener: (...args: any[]) => void,
  ) => EventEmitter;
  public isOnHold: boolean;
  public isMuted: boolean;
  public answered: boolean;
  public direction: 'IN' | 'OUT';

  constructor(
    userAgent: UserAgent,
    direction: 'IN' | 'OUT',
    target: any,
    options?: any,
  ) {
    // Create a new EventEmitter
    const emitter = new EventEmitter();
    // 25 listeners is all we need for now
    // This can be increased as needs be
    emitter.setMaxListeners(25);
    // Event handlers object to be passed to JsSIP
    const eventHandlers: Record<string, any> = {};
    // All possible events which can be emitted
    const events = [
      'peerconnection',
      'connecting',
      'sending',
      'progress',
      'accepted',
      'confirmed',
      'ended',
      'failed',
      'newDTMF',
      'hold',
      'unhold',
      'muted',
      'unmuted',
      'reinvite',
      'update',
      'refer',
      'refersucceeded',
      'referfailed',
      'replaces',
    ];

    // Make UA available for later use
    this.userAgent = userAgent;

    // We'll populate this later
    this.streams = {};
    this.tags = [];
    this.options = {
      ...options,
    };

    this.on = () => emitter;
    this.once = () => emitter;
    if (direction === 'OUT') {
      this.target = target;

      // Convenience function to alias emitter.on() and emitter.once()
      this.on = (e, listener) => emitter.on(e, listener);
      this.once = (e, listener) => emitter.once(e, listener);

      this.options.eventHandlers = eventHandlers;
    } else if (direction === 'IN') {
      const session = target;

      // Convenience function to alias session.on() and session.once()
      this.on = (e, listener) => session.on(e, listener);
      this.once = (e, listener) => session.once(e, listener);

      this.session = session;
    }

    // For each event, bind an emitter to that event's handler and
    // set up a listener for that event to call a local function of
    // the same name (prefixed with '_', suffixed with 'EventHandler')
    events.forEach((e) => {
      eventHandlers[e] = (passedEvent: any) => {
        emitter.emit(e, passedEvent);
      };

      const eventHandlerName = `_${e}EventHandler`;
      const eventHandler = (this as any)[eventHandlerName];

      if (typeof eventHandler === 'function') {
        this.on(e, (passedEvent) => {
          // We have to pass a reference to 'this' in
          // to the event handler otherwise it may
          // not be able to access the call
          eventHandler(this, passedEvent);
        });
      } else {
        this.on(e, (passedEvent) => {
          console.debug(`emitter ${e} event:`, passedEvent);
        });
      }
    });

    this.emitter = emitter;

    this.isOnHold = false;
    this.isMuted = false;
    this.answered = false;
    this.direction = direction;
  }

  _buildSipURI(target: string) {
    return `sip:${target}@${this.userAgent.domain}`;
  }

  invite() {
    // Initiates the call
    this.session = this.userAgent.call(
      this._buildSipURI(this.target),
      this.options,
    );
  }

  connect(options?: any) {
    const mergedOptions = { ...this.options, ...options };
    try {
      this.session.answer(mergedOptions);
    } catch (err) {
      console.error('[Call] session.answer threw:', err);
      throw err;
    }
  }

  refer(number: string, options: any) {
    // Transfers the call
    const target = number ? this._buildSipURI(number) : '';
    const referer: any = this.session.refer(target, options);
    const { emitter } = this;

    referer.on('requestSucceeded', (e: any) => {
      if (this.direction === 'IN') {
        this.session.emit('refersucceeded', e);
      } else {
        emitter.emit('refersucceeded', e);
      }
      // RTCSession.C.STATUS_TERMINATED === 8
      if (this.session.status !== 8) {
        this.session.terminate();
      }
      this.userAgent.removeOngoingCall(this.session.id);
    });

    referer.on('requestFailed', (e: any) => {
      if (this.direction === 'IN') {
        this.session.emit('referfailed', e);
      } else {
        emitter.emit('referfailed', e);
      }
    });

    return referer;
  }

  sendDTMF(tone: string) {
    this.session.sendDTMF(tone);
  }

  _attachRemoteTrack(call: any, track: MediaStreamTrack) {
    // Bind immediately: remote tracks arrive `muted` and unmute once RTP flows,
    // but srcObject is a live sink, so waiting on the (unreliable) 'unmute'
    // event only risks missing playback.
    call.streams.remote = new MediaStream([track]);
    call._bindRemoteAudioElement();
    // Re-bind on unmute as a safety net.
    track.addEventListener('unmute', () => {
      call._bindRemoteAudioElement();
    });
  }

  _peerconnectionEventHandler(call: any, e: any) {
    if (call.direction === 'IN') {
      const pc = e?.peerconnection ?? call.session.connection;
      pc.addEventListener('track', (event: any) => {
        if (event.track.kind !== 'audio') return;
        call._attachRemoteTrack(call, event.track);
      });
    }
  }

  _acceptedEventHandler(call: any) {
    call.startTime = new Date();
    call.answered = true;
  }

  _connectingEventHandler(call: any) {
    if (call.direction === 'OUT') {
      const pc = call.session.connection;
      pc.addEventListener('track', (event: any) => {
        if (event.track.kind !== 'audio') return;
        call._attachRemoteTrack(call, event.track);
      });
      call.userAgent.addOngoingCall(call);
    }
  }

  bye() {
    this.session.terminate();
  }

  _endedEventHandler(call: any) {
    call.endTime = new Date();
    call.userAgent.removeOngoingCall(call.session.id);
  }

  _failedEventHandler(call: any, e: any) {
    console.warn('[Call] call failed', {
      direction: call.direction,
      cause: e?.cause,
      message: e?.message,
    });
    call.userAgent.removeOngoingCall(call.session.id);
  }

  hold() {
    this.session.hold();
  }

  _holdEventHandler(call: any) {
    call.isOnHold = true;
    call._unbindRemoteAudioElements();
  }

  unhold() {
    const activeCall = this.userAgent.getActiveCall();
    const call = this.session;

    if (activeCall) {
      if (activeCall.answered) {
        activeCall.once('hold', () => {
          call.unhold();
        });
        activeCall.hold();
      } else {
        call.unhold();
      }
    } else {
      call.unhold();
    }
  }

  _unholdEventHandler(call: any) {
    call.isOnHold = false;
    call._bindRemoteAudioElement();
  }

  mute() {
    this.session.mute();
  }

  _mutedEventHandler(call: any) {
    call.isMuted = true;
  }

  unmute() {
    this.session.unmute();
  }

  _unmutedEventHandler(call: any) {
    call.isMuted = false;
  }

  _unbindRemoteAudioElements() {
    const remoteAudio = this.userAgent.audio.remote;
    if (remoteAudio) {
      remoteAudio.audioElement.pause();
      remoteAudio.audioElement.srcObject = null;
    }
  }

  async _bindRemoteAudioElement() {
    const remoteAudio = this.userAgent.audio.remote;
    const stream = this.streams.remote;
    if (!remoteAudio || !stream) return;
    const audioEl = remoteAudio.audioElement;
    const { outputDeviceId } = remoteAudio;
    audioEl.srcObject = stream;
    if (outputDeviceId) {
      try {
        await (audioEl as any).setSinkId?.(outputDeviceId);
      } catch (err) {
        console.warn('[Call] setSinkId failed:', err);
      }
    }
    try {
      await audioEl.play();
    } catch (err) {
      console.warn('[Call] remote audioElement play() failed:', err);
    }
  }
}

export default Call;
